import { useState, useEffect, useRef } from "react";

const API_BASE = "http://flap.sakarhomes.com.np:30177/api/device";
const DEFAULT_API_KEY = "flap-key-001";

// ── Mock / live fetch helper ─────────────────────────────────────────────────
async function apiFetch(path, opts = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { "Content-Type": "application/json" },
    ...opts,
  });
  return res.json();
}

// ── Pulse dot ────────────────────────────────────────────────────────────────
function PulseDot({ color = "#00ff88" }) {
  return (
    <span style={{ position: "relative", display: "inline-block", width: 10, height: 10 }}>
      <span style={{
        position: "absolute", inset: 0, borderRadius: "50%",
        background: color, opacity: 0.4,
        animation: "ping 1.4s cubic-bezier(0,0,0.2,1) infinite",
      }} />
      <span style={{
        position: "absolute", inset: "2px", borderRadius: "50%", background: color,
      }} />
    </span>
  );
}

// ── OLED Display Mockup ───────────────────────────────────────────────────────
function OledDisplay({ line1, line2, line3, status }) {
  const statusColor = {
    ok: "#00ff88", unknown: "#facc15", lost: "#f97316", defect: "#ef4444", error: "#ef4444",
  }[status] || "#888";

  return (
    <div style={{
      background: "#0a0a0a",
      border: `1px solid ${statusColor}33`,
      borderRadius: 6,
      padding: "14px 16px",
      fontFamily: "'Courier New', monospace",
      fontSize: 13,
      boxShadow: `0 0 20px ${statusColor}22, inset 0 0 30px #000`,
      minHeight: 88,
      position: "relative",
      overflow: "hidden",
    }}>
      {/* scanline overlay */}
      <div style={{
        position: "absolute", inset: 0, backgroundImage:
          "repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.18) 2px, rgba(0,0,0,0.18) 4px)",
        pointerEvents: "none", zIndex: 1,
      }} />
      <div style={{ position: "relative", zIndex: 2 }}>
        <div style={{ color: statusColor, fontWeight: "bold", letterSpacing: 1, marginBottom: 4 }}>
          {line1 || "—"}
        </div>
        <div style={{ color: "#aaa" }}>{line2 || ""}</div>
        <div style={{ color: "#666", fontSize: 11 }}>{line3 || ""}</div>
      </div>
    </div>
  );
}

// ── Live Tap Feed ─────────────────────────────────────────────────────────────
function TapFeed({ taps }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current) ref.current.scrollTop = ref.current.scrollHeight; }, [taps]);

  return (
    <div ref={ref} style={{
      height: 220, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6,
      paddingRight: 4,
    }}>
      {taps.length === 0 && (
        <div style={{ color: "#444", fontFamily: "monospace", fontSize: 12, textAlign: "center", marginTop: 80 }}>
          Waiting for card taps…
        </div>
      )}
      {taps.map((t, i) => (
        <div key={i} style={{
          background: "#111",
          border: `1px solid ${t.status === "ok" ? "#00ff8833" : "#ef444433"}`,
          borderRadius: 6,
          padding: "8px 12px",
          display: "flex",
          gap: 12,
          alignItems: "flex-start",
          animation: i === taps.length - 1 ? "fadeSlide 0.4s ease" : "none",
        }}>
          <span style={{
            fontSize: 18, lineHeight: 1,
            filter: "drop-shadow(0 0 4px currentColor)",
            color: t.status === "ok" ? "#00ff88" : t.status === "unknown" ? "#facc15" : "#ef4444",
          }}>
            {t.status === "ok" ? "✓" : t.status === "unknown" ? "?" : "✗"}
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
              <span style={{ color: "#e2e2e2", fontFamily: "monospace", fontSize: 13, fontWeight: "bold" }}>
                {t.display?.line1 || t.message || "—"}
              </span>
              <span style={{ color: "#444", fontSize: 11, fontFamily: "monospace", whiteSpace: "nowrap", marginLeft: 8 }}>
                {t.time}
              </span>
            </div>
            {t.display?.line2 && (
              <div style={{ color: "#888", fontFamily: "monospace", fontSize: 11 }}>{t.display.line2}</div>
            )}
            {t.uid && (
              <div style={{ color: "#444", fontFamily: "monospace", fontSize: 10, marginTop: 2 }}>UID: {t.uid}</div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Device Card ───────────────────────────────────────────────────────────────
function DeviceCard({ device, taps, onSimulate, simLoading }) {
  const isOnline = device.isActive;
  const lastSeen = device.lastSeen
    ? new Date(device.lastSeen).toLocaleString() : "Never";
  const lastTap = taps.find(t => t.device_id === device.deviceId);

  return (
    <div style={{
      background: "linear-gradient(135deg, #0f0f0f 0%, #141414 100%)",
      border: "1px solid #222",
      borderRadius: 10,
      padding: 20,
      display: "flex",
      flexDirection: "column",
      gap: 14,
      transition: "border-color 0.2s",
    }}
      onMouseEnter={e => e.currentTarget.style.borderColor = "#333"}
      onMouseLeave={e => e.currentTarget.style.borderColor = "#222"}
    >
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <PulseDot color={isOnline ? "#00ff88" : "#555"} />
            <span style={{ color: "#f0f0f0", fontWeight: 700, fontSize: 15, letterSpacing: 0.5 }}>
              {device.name || device.deviceId}
            </span>
          </div>
          <div style={{ color: "#555", fontSize: 11, fontFamily: "monospace" }}>
            ID: {device.deviceId?.slice(0, 16)}…
          </div>
        </div>
        <span style={{
          background: isOnline ? "#00ff8818" : "#ffffff08",
          color: isOnline ? "#00ff88" : "#444",
          border: `1px solid ${isOnline ? "#00ff8844" : "#222"}`,
          borderRadius: 20, padding: "3px 10px", fontSize: 11, fontFamily: "monospace",
        }}>
          {isOnline ? "ACTIVE" : "OFFLINE"}
        </span>
      </div>

      {/* Meta */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        {[
          ["Type", device.type || "nfc_reader"],
          ["Location", device.location || "—"],
          ["Firmware", device.firmwareVer || "—"],
          ["Last Seen", lastSeen],
        ].map(([k, v]) => (
          <div key={k} style={{ background: "#0a0a0a", borderRadius: 6, padding: "7px 10px" }}>
            <div style={{ color: "#444", fontSize: 10, fontFamily: "monospace", marginBottom: 2 }}>{k}</div>
            <div style={{ color: "#aaa", fontSize: 12, fontFamily: "monospace", wordBreak: "break-all" }}>{v}</div>
          </div>
        ))}
      </div>

      {/* OLED preview of last tap */}
      <div>
        <div style={{ color: "#444", fontSize: 10, fontFamily: "monospace", marginBottom: 6, letterSpacing: 1 }}>
          LAST TAP OUTPUT
        </div>
        <OledDisplay
          line1={lastTap?.display?.line1 || "Ready"}
          line2={lastTap?.display?.line2 || "Tap your card"}
          line3={lastTap?.display?.line3 || ""}
          status={lastTap?.status || "ok"}
        />
      </div>

      {/* Simulate button */}
      <button
        onClick={() => onSimulate(device.deviceId)}
        disabled={simLoading === device.deviceId}
        style={{
          background: simLoading === device.deviceId ? "#111" : "#00ff8810",
          color: simLoading === device.deviceId ? "#444" : "#00ff88",
          border: `1px solid ${simLoading === device.deviceId ? "#222" : "#00ff8844"}`,
          borderRadius: 6, padding: "8px 14px", cursor: "pointer",
          fontFamily: "monospace", fontSize: 12, letterSpacing: 1,
          transition: "all 0.2s",
        }}
        onMouseEnter={e => { if (simLoading !== device.deviceId) { e.target.style.background = "#00ff8820"; e.target.style.borderColor = "#00ff88"; } }}
        onMouseLeave={e => { e.target.style.background = "#00ff8810"; e.target.style.borderColor = "#00ff8844"; }}
      >
        {simLoading === device.deviceId ? "SENDING…" : "⚡ SIMULATE TAP"}
      </button>
    </div>
  );
}

// ── Create Device Modal ───────────────────────────────────────────────────────
function CreateModal({ onClose, onCreated, apiKey, businessId }) {
  const [form, setForm] = useState({
    device_id: crypto.randomUUID().replace(/-/g, ""),
    device_name: "",
    device_type: "nfc_reader",
    location: "",
    firmware_ver: "2.3.1",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleCreate = async () => {
    if (!form.device_name.trim()) { setError("Device name is required"); return; }
    setLoading(true); setError("");
    try {
      const res = await apiFetch("/register", {
        method: "POST",
        body: JSON.stringify({ ...form, api_key: apiKey, business_id: businessId }),
      });
      if (res.status === "error") { setError(res.message); }
      else { onCreated(form, res); onClose(); }
    } catch (e) {
      setError("Connection failed: " + e.message);
    }
    setLoading(false);
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 100,
      display: "flex", alignItems: "center", justifyContent: "center",
      backdropFilter: "blur(4px)",
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: "#0f0f0f",
        border: "1px solid #2a2a2a",
        borderRadius: 12, padding: 28, width: 440, maxWidth: "92vw",
        boxShadow: "0 40px 80px rgba(0,0,0,0.8), 0 0 0 1px #111",
        animation: "modalIn 0.25s cubic-bezier(.34,1.56,.64,1)",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 22 }}>
          <h2 style={{ color: "#f0f0f0", margin: 0, fontSize: 16, fontFamily: "monospace", letterSpacing: 1 }}>
            + NEW DEVICE
          </h2>
          <button onClick={onClose} style={{
            background: "none", border: "none", color: "#555", cursor: "pointer", fontSize: 20, padding: 0,
          }}>✕</button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {[
            ["Device Name", "device_name", "text", "e.g. Entrance Reader"],
            ["Device ID", "device_id", "text", "auto-generated"],
            ["Location", "location", "text", "e.g. Main Entrance"],
            ["Firmware", "firmware_ver", "text", ""],
          ].map(([label, key, type, ph]) => (
            <div key={key}>
              <label style={{ color: "#555", fontSize: 10, fontFamily: "monospace", letterSpacing: 1, display: "block", marginBottom: 5 }}>
                {label}
              </label>
              <input
                type={type}
                value={form[key]}
                onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                placeholder={ph}
                style={{
                  width: "100%", background: "#0a0a0a", border: "1px solid #222",
                  borderRadius: 6, padding: "9px 12px", color: "#e0e0e0",
                  fontFamily: "monospace", fontSize: 12, outline: "none",
                  boxSizing: "border-box",
                  transition: "border-color 0.15s",
                }}
                onFocus={e => e.target.style.borderColor = "#00ff8844"}
                onBlur={e => e.target.style.borderColor = "#222"}
              />
            </div>
          ))}

          <div>
            <label style={{ color: "#555", fontSize: 10, fontFamily: "monospace", letterSpacing: 1, display: "block", marginBottom: 5 }}>
              DEVICE TYPE
            </label>
            <select
              value={form.device_type}
              onChange={e => setForm(f => ({ ...f, device_type: e.target.value }))}
              style={{
                width: "100%", background: "#0a0a0a", border: "1px solid #222",
                borderRadius: 6, padding: "9px 12px", color: "#e0e0e0",
                fontFamily: "monospace", fontSize: 12, outline: "none", boxSizing: "border-box",
              }}
            >
              {["nfc_reader", "uhf_reader", "qr_scanner", "access_control"].map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>

          {error && (
            <div style={{ color: "#ef4444", fontFamily: "monospace", fontSize: 12, background: "#ef444411", borderRadius: 6, padding: "8px 12px" }}>
              ⚠ {error}
            </div>
          )}

          <button
            onClick={handleCreate}
            disabled={loading}
            style={{
              marginTop: 6,
              background: loading ? "#111" : "linear-gradient(135deg, #00ff8820, #00cc6620)",
              color: loading ? "#444" : "#00ff88",
              border: `1px solid ${loading ? "#222" : "#00ff8855"}`,
              borderRadius: 6, padding: "10px 14px", cursor: loading ? "not-allowed" : "pointer",
              fontFamily: "monospace", fontSize: 13, letterSpacing: 1, fontWeight: "bold",
            }}
          >
            {loading ? "REGISTERING…" : "REGISTER DEVICE →"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main App ──────────────────────────────────────────────────────────────────
export default function DeviceManager() {
  const [devices, setDevices] = useState([]);
  const [taps, setTaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [serverStatus, setServerStatus] = useState("checking");
  const [showCreate, setShowCreate] = useState(false);
  const [simLoading, setSimLoading] = useState(null);
  const [apiKey] = useState(DEFAULT_API_KEY);
  const [businessId] = useState("69c05443289f43cc218c3262");
  const [tab, setTab] = useState("devices");
  const pollRef = useRef(null);

  // Ping & load devices
  const checkServer = async () => {
    try {
      const r = await apiFetch("/ping");
      setServerStatus(r.status === "ok" ? "online" : "offline");
    } catch { setServerStatus("offline"); }
  };

  const loadDevices = async () => {
    setLoading(true);
    try {
      // We'll simulate loading devices from business (the register endpoint upserts into Business.devices)
      // In real usage you'd have GET /api/device/list?business_id=...&api_key=...
      // For now show a placeholder device list
      const res = await apiFetch(`/lookup?api_key=${apiKey}&business_id=${businessId}&uid=00000000`);
      // Just use as connectivity check; devices come from local state
    } catch {}
    setLoading(false);
  };

  useEffect(() => {
    checkServer();
    loadDevices();
    // Poll server status every 15s
    pollRef.current = setInterval(checkServer, 15000);
    return () => clearInterval(pollRef.current);
  }, []);

  const handleSimulate = async (deviceId) => {
    setSimLoading(deviceId);
    // Simulate a random UID tap
    const fakeUid = Array.from({ length: 4 }, () =>
      Math.floor(Math.random() * 256).toString(16).padStart(2, "0").toUpperCase()
    ).join("");
    try {
      const res = await apiFetch("/tap", {
        method: "POST",
        body: JSON.stringify({
          device_id: deviceId,
          api_key: apiKey,
          business_id: businessId,
          tag_uid: fakeUid,
          tag_type: "MIFARE",
          type: "checkin",
        }),
      });
      const tap = {
        ...res,
        device_id: deviceId,
        uid: fakeUid,
        time: new Date().toLocaleTimeString(),
      };
      setTaps(prev => [...prev.slice(-49), tap]);
    } catch (e) {
      setTaps(prev => [...prev.slice(-49), {
        status: "error", message: e.message,
        display: { line1: "Sim Error", line2: e.message.slice(0, 21) },
        device_id: deviceId, uid: fakeUid, time: new Date().toLocaleTimeString(),
      }]);
    }
    setSimLoading(null);
  };

  const handleDeviceCreated = (form, res) => {
    setDevices(prev => {
      const exists = prev.findIndex(d => d.deviceId === form.device_id);
      const newDevice = {
        deviceId: form.device_id,
        name: form.device_name,
        type: form.device_type,
        location: form.location,
        firmwareVer: form.firmware_ver,
        isActive: true,
        lastSeen: new Date().toISOString(),
        registeredAt: new Date().toISOString(),
      };
      if (exists >= 0) { const arr = [...prev]; arr[exists] = newDevice; return arr; }
      return [...prev, newDevice];
    });
  };

  const statusColor = { online: "#00ff88", offline: "#ef4444", checking: "#facc15" }[serverStatus];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Mono:wght@400;700&family=Syne:wght@400;600;700;800&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: #080808; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: #0a0a0a; }
        ::-webkit-scrollbar-thumb { background: #222; border-radius: 2px; }
        @keyframes ping {
          75%, 100% { transform: scale(2); opacity: 0; }
        }
        @keyframes fadeSlide {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes modalIn {
          from { opacity: 0; transform: scale(0.92) translateY(16px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes scanLine {
          0%   { top: 0; }
          100% { top: 100%; }
        }
        @keyframes glow {
          0%, 100% { box-shadow: 0 0 20px #00ff8822; }
          50%       { box-shadow: 0 0 40px #00ff8844; }
        }
      `}</style>

      <div style={{
        minHeight: "100vh",
        background: "#080808",
        color: "#e0e0e0",
        fontFamily: "'Syne', sans-serif",
      }}>

        {/* Header */}
        <div style={{
          borderBottom: "1px solid #161616",
          padding: "0 28px",
          height: 58,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          position: "sticky", top: 0,
          background: "#080808cc",
          backdropFilter: "blur(10px)",
          zIndex: 50,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{
              width: 32, height: 32,
              background: "linear-gradient(135deg, #00ff88, #00cc66)",
              borderRadius: 8,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 16,
              boxShadow: "0 0 16px #00ff8844",
            }}>⬡</div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 15, letterSpacing: 0.5, lineHeight: 1 }}>
                FLAP
              </div>
              <div style={{ color: "#444", fontSize: 10, fontFamily: "monospace", letterSpacing: 1 }}>
                DEVICE MANAGER
              </div>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <PulseDot color={statusColor} />
              <span style={{ color: statusColor, fontSize: 11, fontFamily: "monospace", letterSpacing: 1 }}>
                SERVER {serverStatus.toUpperCase()}
              </span>
            </div>
            <button
              onClick={() => setShowCreate(true)}
              style={{
                background: "linear-gradient(135deg, #00ff8820, #00cc6618)",
                color: "#00ff88",
                border: "1px solid #00ff8844",
                borderRadius: 7, padding: "7px 16px",
                fontFamily: "monospace", fontSize: 12, letterSpacing: 1,
                cursor: "pointer", fontWeight: "bold",
                transition: "all 0.15s",
              }}
              onMouseEnter={e => { e.target.style.background = "#00ff8828"; e.target.style.boxShadow = "0 0 16px #00ff8822"; }}
              onMouseLeave={e => { e.target.style.background = "#00ff8820"; e.target.style.boxShadow = "none"; }}
            >
              + ADD DEVICE
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ padding: "16px 28px 0", display: "flex", gap: 4, borderBottom: "1px solid #161616" }}>
          {[["devices", "DEVICES"], ["feed", "LIVE FEED"], ["firmware", "FIRMWARE"]].map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)} style={{
              background: "none", border: "none",
              borderBottom: `2px solid ${tab === key ? "#00ff88" : "transparent"}`,
              color: tab === key ? "#00ff88" : "#444",
              padding: "10px 16px 12px",
              fontFamily: "monospace", fontSize: 12, letterSpacing: 1,
              cursor: "pointer", transition: "color 0.15s",
            }}>
              {label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{ padding: 28, maxWidth: 1200, margin: "0 auto" }}>

          {tab === "devices" && (
            <>
              {devices.length === 0 && !loading && (
                <div style={{
                  textAlign: "center", paddingTop: 80, color: "#333",
                  fontFamily: "monospace",
                }}>
                  <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.3 }}>⬡</div>
                  <div style={{ fontSize: 14, marginBottom: 8 }}>No devices registered yet</div>
                  <div style={{ fontSize: 12, color: "#282828" }}>
                    Click <span style={{ color: "#00ff8866" }}>+ ADD DEVICE</span> to register your first reader
                  </div>
                </div>
              )}

              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))",
                gap: 16,
              }}>
                {devices.map(d => (
                  <DeviceCard
                    key={d.deviceId}
                    device={d}
                    taps={taps}
                    onSimulate={handleSimulate}
                    simLoading={simLoading}
                  />
                ))}
              </div>
            </>
          )}

          {tab === "feed" && (
            <div style={{ maxWidth: 700 }}>
              <div style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                marginBottom: 16,
              }}>
                <div style={{ color: "#555", fontFamily: "monospace", fontSize: 12, letterSpacing: 1 }}>
                  LIVE TAP EVENTS — {taps.length} recorded
                </div>
                {taps.length > 0 && (
                  <button onClick={() => setTaps([])} style={{
                    background: "none", border: "1px solid #222",
                    color: "#444", borderRadius: 4, padding: "4px 10px",
                    fontFamily: "monospace", fontSize: 11, cursor: "pointer",
                  }}>
                    CLEAR
                  </button>
                )}
              </div>
              <TapFeed taps={taps} />
            </div>
          )}

          {tab === "firmware" && (
            <div style={{ maxWidth: 680 }}>
              <div style={{
                background: "#0f0f0f",
                border: "1px solid #1a1a1a",
                borderRadius: 10,
                padding: 24,
              }}>
                <div style={{ color: "#555", fontFamily: "monospace", fontSize: 11, letterSpacing: 1, marginBottom: 16 }}>
                  ESP8266 FIRMWARE CONFIG
                </div>
                <div style={{ fontFamily: "monospace", fontSize: 12, lineHeight: 1.8 }}>
                  {[
                    ["WIFI_SSID", "flap_2.4"],
                    ["WIFI_PASS", "••••••••••"],
                    ["DEVICE_ID", "ccc853990e8670ac…"],
                    ["API_KEY", "flap-key-001"],
                    ["BUSINESS_ID", businessId],
                    ["TAP_URL", "http://flap.sakarhomes.com.np:30177/api/device/tap"],
                    ["PING_URL", "…/api/device/ping"],
                    ["REGISTER_URL", "…/api/device/register"],
                    ["COOLDOWN_MS", "3000"],
                  ].map(([k, v]) => (
                    <div key={k} style={{ display: "flex", gap: 16, padding: "6px 0", borderBottom: "1px solid #111" }}>
                      <span style={{ color: "#00ff8888", minWidth: 140 }}>{k}</span>
                      <span style={{ color: "#888", wordBreak: "break-all" }}>{v}</span>
                    </div>
                  ))}
                </div>

                <div style={{
                  marginTop: 20,
                  background: "#0a0a0a",
                  borderRadius: 8,
                  padding: "14px 16px",
                  border: "1px solid #1a1a1a",
                }}>
                  <div style={{ color: "#555", fontFamily: "monospace", fontSize: 10, marginBottom: 10, letterSpacing: 1 }}>
                    WIRING — NodeMCU ↔ RC522
                  </div>
                  {[
                    ["SS (SDA)", "D8 / GPIO15"],
                    ["SCK", "D5 / GPIO14"],
                    ["MOSI", "D7 / GPIO13"],
                    ["MISO", "D6 / GPIO12"],
                    ["RST", "D3 / GPIO0"],
                    ["3.3V", "3V3"],
                    ["GND", "GND"],
                    ["OLED SDA", "D2 / GPIO4"],
                    ["OLED SCL", "D1 / GPIO5"],
                  ].map(([a, b]) => (
                    <div key={a} style={{ display: "flex", gap: 8, fontFamily: "monospace", fontSize: 11, padding: "3px 0" }}>
                      <span style={{ color: "#facc15", minWidth: 100 }}>{a}</span>
                      <span style={{ color: "#444" }}>→</span>
                      <span style={{ color: "#888" }}>{b}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {showCreate && (
        <CreateModal
          onClose={() => setShowCreate(false)}
          onCreated={handleDeviceCreated}
          apiKey={apiKey}
          businessId={businessId}
        />
      )}
    </>
  );
}